/**
 * Email settings (Story 26, task 104): the role-level on/off for Admins,
 * Analysts and Viewers, and the lock that makes the role-level setting
 * final for Analysts and Viewers.
 *
 * Since task 208 (Story 48) these are an organization's settings, resolved
 * through the tree for each recipient from their home: the card shows the
 * caller's home organization, what applies there and which organization
 * set it. With `manage_org` there it can override (this organization's own
 * value) or revert to inherited; Simulint's value is the system's and has
 * nothing to revert to. It lives on Preferences: settings are configured
 * where they are felt, not on the Users page. The card edits a draft and
 * saves it whole, with the version it read.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ApiError,
  getOrgSetting,
  putOrgSetting,
  revertOrgSetting,
  type OrgSettingView,
  type PlatformRole,
} from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { useAccessToken } from '../auth/useAccessToken';
import { Switch } from './ResponseSettingsModal';
import { ErrorState, Loading } from './states';
import { absoluteTime, timeAgo } from '@bluesphere/mdr-alert-panel';
import { ROLE_LABELS, ROLES } from '../lib/users';

interface EmailValue {
  role_email: Record<PlatformRole, 'on' | 'off'>;
  self_service_locked: boolean;
}

export function EmailSettingsCard({
  orgId,
  orgName,
  isRoot,
  canEdit,
  nameOf,
}: {
  /** The caller's home organization. */
  orgId: string;
  orgName: string;
  /** Simulint's own: no parent to inherit from. */
  isRoot: boolean;
  /** `manage_org` at `orgId`. */
  canEdit: boolean;
  /** An organization's name, for "Set by". */
  nameOf: (orgId: string) => string;
}) {
  const idToken = useAccessToken();
  const queryClient = useQueryClient();
  const key = ['org-settings', orgId, 'email'];

  const settingsQuery = useQuery({
    queryKey: key,
    queryFn: () => getOrgSetting(orgId, 'email', idToken),
    enabled: Boolean(idToken),
  });

  const [draft, setDraft] = useState<EmailValue | null>(null);
  // A fresh read (first load, or after a save) resets the draft.
  useEffect(() => {
    if (settingsQuery.data) setDraft(toValue(settingsQuery.data));
  }, [settingsQuery.data]);

  const onSaved = (view: OrgSettingView) => {
    queryClient.setQueryData(key, view);
    // The caller's own Preferences read the role setting and the lock.
    void queryClient.invalidateQueries({ queryKey: ['me', 'preferences'] });
  };
  const mutation = useMutation<OrgSettingView, Error, EmailValue>({
    mutationFn: (value) =>
      putOrgSetting(
        orgId,
        'email',
        { value: { ...value }, expected_version: settingsQuery.data?.version ?? 0 },
        idToken,
      ),
    onSuccess: onSaved,
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
  const revert = useMutation<OrgSettingView, Error, void>({
    mutationFn: () => revertOrgSetting(orgId, 'email', idToken),
    onSuccess: onSaved,
  });

  if (settingsQuery.isLoading || (settingsQuery.data && !draft)) {
    return (
      <div className="bs-card">
        <Loading height="h-16" />
      </div>
    );
  }
  if (settingsQuery.error || !settingsQuery.data || !draft) {
    return (
      <ErrorState
        title="Could not load the email settings"
        error={settingsQuery.error}
        onRetry={() => void settingsQuery.refetch()}
      />
    );
  }

  const saved = settingsQuery.data;
  const dirty = JSON.stringify(draft) !== JSON.stringify(toValue(saved));
  const busy = mutation.isPending || revert.isPending;
  const own = saved.own !== undefined;
  const setHere = saved.source_node_id === orgId;

  return (
    <section className="bs-card p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-bluesphere-textOnLight">
            Email for {orgName}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-bluesphere-muted">
            Who at {orgName} receives alert and action emails, by role. With a role switched off,
            nobody in it is emailed whatever their own preferences say. Scope still applies: nobody
            hears about a Client outside theirs.
          </p>
        </div>
        <p className="text-xs text-bluesphere-muted">
          {setHere ? 'Set here' : `Set by ${nameOf(saved.source_node_id)}`}
          {own && saved.updated_at && (
            <>
              {' · '}
              <span title={absoluteTime(saved.updated_at)}>{timeAgo(saved.updated_at)}</span>
            </>
          )}
        </p>
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="space-y-4">
          {ROLES.map((role) => (
            <Switch
              key={role}
              checked={draft.role_email[role] === 'on'}
              disabled={busy || !canEdit}
              onChange={(on) =>
                setDraft({
                  ...draft,
                  role_email: { ...draft.role_email, [role]: on ? 'on' : 'off' },
                })
              }
              label={`Email ${ROLE_LABELS[role]}s`}
              hint={
                role === 'viewer'
                  ? 'Viewers are not enrolled by default; this reaches those who opt in.'
                  : undefined
              }
            />
          ))}
        </div>
        <Switch
          checked={draft.self_service_locked}
          disabled={busy || !canEdit}
          onChange={(locked) => setDraft({ ...draft, self_service_locked: locked })}
          label="Lock for Analysts and Viewers"
          hint="Their role setting becomes final: their own choices are ignored (not erased) and their Preferences page is read-only. Admins keep their own choices."
        />
      </div>

      {(mutation.error || revert.error) && (
        <div className="mt-4">
          <ErrorState
            title="Could not save the email settings"
            error={mutation.error ?? revert.error}
          />
        </div>
      )}

      {canEdit && (
        <div className="mt-5 flex flex-wrap gap-3">
          <PillButton size="sm" disabled={!dirty || busy} onClick={() => mutation.mutate(draft)}>
            {busy ? 'Saving…' : own ? 'Save' : `Override for ${orgName}`}
          </PillButton>
          {dirty && !busy && (
            <PillButton
              size="sm"
              variant="outline"
              onClick={() => {
                mutation.reset();
                setDraft(toValue(saved));
              }}
            >
              Discard
            </PillButton>
          )}
          {own && !isRoot && !dirty && (
            <PillButton size="sm" variant="outline" disabled={busy} onClick={() => revert.mutate()}>
              Revert to inherited
            </PillButton>
          )}
        </div>
      )}
    </section>
  );
}

function toValue(view: OrgSettingView): EmailValue {
  const v = view.effective as Partial<EmailValue>;
  const roles = (v.role_email ?? {}) as Partial<Record<PlatformRole, 'on' | 'off'>>;
  return {
    role_email: {
      admin: roles.admin ?? 'on',
      analyst: roles.analyst ?? 'on',
      viewer: roles.viewer ?? 'on',
    },
    self_service_locked: Boolean(v.self_service_locked),
  };
}
